// ── Historique des prix (fichiers /data/history/*.json produits par le script) ──

export type GpuHistory = {
  gpu: string; updated: string;
  /** series : dernier prix de chaque jour ; hourly : chaque relevé horaire des 7 derniers jours. */
  providers: Record<string, { name: string; url: string; series: [string, number][]; hourly?: [string, number][] }>;
};
export type HistoryIndex = {
  updated: string;
  gpus: Record<string, { firstDay: string; days: number; latest: number; avg30: number }>;
};

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const isDay = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
const isHour = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:00Z$/.test(v);
const isPrice = (v: unknown): v is number => typeof v === "number" && v > 0.01 && v < 100;

/** Valide un historique de GPU ; les points ou fournisseurs invalides sont écartés. */
export function validateHistory(data: unknown): GpuHistory | null {
  if (!isObj(data) || typeof data.gpu !== "string" || typeof data.updated !== "string" || !isObj(data.providers)) return null;
  const providers: GpuHistory["providers"] = {};
  for (const [id, p] of Object.entries(data.providers)) {
    if (!isObj(p) || typeof p.name !== "string" || typeof p.url !== "string" || !p.url.startsWith("https://") || !Array.isArray(p.series)) continue;
    const series = p.series.filter((pt): pt is [string, number] => Array.isArray(pt) && isDay(pt[0]) && isPrice(pt[1]));
    const hourly = Array.isArray(p.hourly) ? p.hourly.filter((pt): pt is [string, number] => Array.isArray(pt) && isHour(pt[0]) && isPrice(pt[1])) : [];
    if (series.length) providers[id] = { name: p.name, url: p.url, series, ...(hourly.length ? { hourly } : {}) };
  }
  return Object.keys(providers).length ? { gpu: data.gpu, updated: data.updated, providers } : null;
}

export function validateHistoryIndex(data: unknown): HistoryIndex | null {
  if (!isObj(data) || typeof data.updated !== "string" || !isObj(data.gpus)) return null;
  const gpus: HistoryIndex["gpus"] = {};
  for (const [id, g] of Object.entries(data.gpus)) {
    if (isObj(g) && isDay(g.firstDay) && typeof g.days === "number" && isPrice(g.latest) && isPrice(g.avg30)) {
      gpus[id] = { firstDay: g.firstDay, days: g.days, latest: g.latest, avg30: g.avg30 };
    }
  }
  return { updated: data.updated, gpus };
}
